import React from 'react';
import { Grid, Tooltip, IconButton } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CustomAccordion from '../ui/CustomAccordion';
import InfoField from './InfoField';
import { getNestedProperty, formatFieldValue } from '../utils';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

interface PersonalInformationProps {
  profile: Record<string, any>;
  fieldConfigs?: any;
  headerText?: string;
  leadId?: string | number;
}

const PersonalInformationSection: React.FC<PersonalInformationProps> = ({
  profile,
  fieldConfigs,
  headerText,
  leadId,
}) => {
  const { sitecoreContext } = useSitecoreContext();
  const dateTimeFormat: string =
    (sitecoreContext as any)?.siteSettings?.DateTimeFormat?.value || 'MM/DD/YYYY HH:MM:SS';
  const dateFormat: string =
    (sitecoreContext as any)?.siteSettings?.DateFormat?.value || 'MM/DD/YYYY';

  const [expanded, setExpanded] = React.useState(true);

  const DATE_TIME_KEYS = new Set(['registrationDateTime']);
  const DATE_ONLY_KEYS = new Set(['dob']);
  const PERCENTAGE_KEYS = new Set(['matchPercentage', 'patientMatchingScore']);

  const getFieldFormat = (key: string): string => {
    if (DATE_TIME_KEYS.has(key)) return dateTimeFormat;
    if (DATE_ONLY_KEYS.has(key)) return dateFormat;
    return dateFormat;
  };

  const getFieldValue = (key: string, rawValue: string | undefined): string | undefined => {
    if (PERCENTAGE_KEYS.has(key) && rawValue !== undefined && rawValue !== '') {
      return `${Math.min(Number(rawValue) || 0, 100)}%`;
    }
    return formatFieldValue(
      key,
      rawValue,
      getFieldFormat(key),
      new Set([...DATE_TIME_KEYS, ...DATE_ONLY_KEYS])
    );
  };

  const defaultFields = [
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'dob', label: 'Date of Birth' },
    { key: 'gender', label: 'Gender' },
    { key: 'email', label: 'Email' },
    { key: 'phoneNumber', label: 'Phone Number' },
    { key: 'address', label: 'Address' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'zipcode', label: 'Zipcode' },
  ];

  const fields = fieldConfigs
    ? Array.isArray(fieldConfigs)
      ? fieldConfigs.map((config: any) => ({
          key: config.fields.ExpandedViewItemKey.value,
          label: config.fields.ExpandedViewItemLabel.value,
        }))
      : defaultFields
    : defaultFields;

  return (
    <CustomAccordion
      title={headerText || 'Personal Information'}
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      summaryActions={
        leadId != null ? (
          <Tooltip title={`Lead ID: ${leadId}`} placement="top" arrow>
            <IconButton
              size="small"
              sx={{ p: 0.25 }}
              aria-label="Show lead ID"
              onClick={(e: React.MouseEvent) => e.stopPropagation()}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        ) : undefined
      }
      sx={{
        mt: 1,
        '& .MuiAccordionSummary-root': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-root.Mui-expanded': {
          minHeight: '40px',
          py: 0.5,
        },
        '& .MuiAccordionSummary-content': {
          margin: '0 !important',
        },
        '& .MuiAccordionSummary-content.Mui-expanded': {
          margin: '0 !important',
        },
      }}
      detailsSx={{ py: 1, px: 2 }}
    >
      <Grid container spacing={1.5}>
        {fields.map((field, index) => (
          <Grid item xs={12} sm={6} md={3} key={field.key || index}>
            <InfoField
              label={field.label}
              value={getFieldValue(field.key, getNestedProperty(profile, field.key))}
            />
          </Grid>
        ))}
      </Grid>
    </CustomAccordion>
  );
};

export default PersonalInformationSection;
